/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TableSortLabel,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";

import {
  tableHeaderStyles,
  tableSortLabelStyles,
  tableCellStyles,
  scrollbarStyles,
  StyledNoDataText,
  StyledTableCell,
} from "../styles";
import { StyledPaginationContainer } from "../../../components/index_table/styles";
import { useFillViewportHeight } from "../../../hooks/useFillViewportHeight";

const DataTable = ({
  fetchingDataLoader,
  searchLoading = false,
  hasSearchTerm = false,
  columnOptions,
  formattedOrders,
  sortedOrders,
  orderBy,
  order,
  handleSort,
  visibleColumns,
  loadingImg,
  totalCount,
  tableId,
  handleOpen,
  setSelectedData,
  page,
  setPage,
  enableRowClick = false,
  nonSortableColumns = [],
  emptyStateText = "No Data Exists",
}) => {
  const storageKey = `tableWidths-${tableId || "default"}`;

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) return JSON.parse(saved);

    const defaultWidths = columnOptions.reduce((acc, col) => {
      acc[col] = 150;
      return acc;
    }, {});
    return defaultWidths;
  });
  const [scrollWidth, setScrollWidth] = useState(0);
  const tableRef = useRef(null);
  const fakeScrollRef = useRef(null);
  const tableFooterRef = useRef(null);

  const containerMaxHeight = useFillViewportHeight({
    containerRef: tableRef,
    bottomRef: tableFooterRef,
  });
  const resizingRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });

  // Handles column resizing
  const startResizing = (key, e) => {
    e.preventDefault();
    resizingRef.current = {
      columnKey: key,
      startX: e.clientX,
      startWidth: columnWidths[key],
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;
    const newWidth = Math.max(80, startWidth + e.clientX - startX);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  }, []);

  const handleMouseUp = useCallback(
    (e) => {
      const { columnKey, startX, startWidth } = resizingRef.current;
      if (!columnKey) return;

      const finalWidth = Math.max(80, startWidth + e.clientX - startX);

      const updated = { ...columnWidths, [columnKey]: finalWidth };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      setColumnWidths(updated);

      resizingRef.current = { columnKey: null, startX: 0, startWidth: 0 };
      document.body.style.cursor = "";

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    },
    [columnWidths, storageKey, handleMouseMove]
  );

  const selectedData = (data) => {
    if (enableRowClick) {
      setSelectedData(data?.order_id);
      handleOpen();
    }
  };

  // Handles horizontal scroll
  const handleMainScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    fakeScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
  };

  const handleFakeScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    tableRef.current.scrollLeft = fakeScrollRef.current.scrollLeft;
  };

  useEffect(() => {
    if(!tableRef.current) return;

    if (tableRef.current) {
      setScrollWidth(tableRef.current.scrollWidth);
    }
  }, [columnWidths, sortedOrders]);

  useEffect(() => {
    const onResize = () => {
      if (tableRef.current) {
        setScrollWidth(tableRef.current.scrollWidth);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <React.Fragment>
      <Box sx={{ position: "relative" }} data-testid="index-table">
        <TableContainer
          ref={tableRef}
          component={Paper}
          sx={{ 
            mt: 1, 
            boxShadow: "none", 
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: containerMaxHeight,
            // Mirrors StyledTableContainer, which this bare TableContainer cannot
            // inherit: the synced fake scrollbar below is the only horizontal
            // affordance, so the container's own bars stay hidden. Both properties
            // are required - see the note on StyledTableContainer.
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
          onScroll={handleMainScroll}
        >
          {fetchingDataLoader ? (
            <Box align="center" sx={{ alignSelf: "center", py: 3 }}>
              <img style={{ height: 80 }} src={loadingImg} alt="loading..." />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  {columnOptions.map(
                    (col) =>
                      visibleColumns[col] && (
                        <TableCell
                          key={col}
                          sx={{
                            ...tableHeaderStyles,
                            width: `${columnWidths[col]}px`,
                          }}
                        >
                          {!nonSortableColumns.includes(col) ? (
                            <TableSortLabel
                              active={orderBy === col}
                              direction={orderBy === col ? order : "asc"}
                              onClick={() => handleSort(col)}
                              sx={tableSortLabelStyles}
                            >
                              {col}
                            </TableSortLabel>
                          ) : (
                            <span style={{ cursor: "default" }}>
                              {col}
                            </span>
                          )}

                          {/* Column resize handle */}
                          <Box
                            onMouseDown={(e) => startResizing(col, e)}
                            sx={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              height: "100%",
                              width: "6px",
                              cursor: "col-resize",
                              zIndex: 10,
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.1)",
                              },
                            }}
                          />
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {searchLoading && hasSearchTerm ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnOptions.length}
                      sx={{
                        ...tableCellStyles,
                        textAlign: "center",
                      }}
                    >
                      <StyledNoDataText variant="body2">
                        Searching...
                      </StyledNoDataText>
                    </TableCell>
                  </TableRow>
                ) : formattedOrders(sortedOrders).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnOptions.length}
                      sx={tableCellStyles}
                    >
                      <StyledNoDataText
                        variant="body2"
                      >
                        {emptyStateText}
                      </StyledNoDataText>
                    </TableCell>
                  </TableRow>
                ) : (
                  formattedOrders(sortedOrders)
                    .map((order) => (
                      <TableRow
                        onClick={() => selectedData(order)}
                        key={order.id}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f7f7f7" },
                        }}
                      >
                        {columnOptions.map(
                          (col) =>
                            visibleColumns[col] && (
                              <StyledTableCell
                                key={col}
                                width={columnWidths[col]}
                              >
                                {
                                  order[col
                                    .toLowerCase()
                                    .replace(/ /g, "_")]
                                }
                              </StyledTableCell>
                            )
                        )}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Measured block below the table; its height is reserved from the available space. */}
        <Box ref={tableFooterRef} sx={{ display: 'flow-root' }}>
          {!fetchingDataLoader && (
            <React.Fragment>
              <Box
                ref={fakeScrollRef}
                onScroll={handleFakeScroll}
                sx={scrollbarStyles}
              >
                <Box
                  sx={{
                    width: scrollWidth || '100%',
                    height: "1px",
                  }}
                />
              </Box>
              <StyledPaginationContainer>
                <Pagination 
                  page={page} 
                  count={totalCount} 
                  onChange={(_event, page) => setPage(page)}
                  data-testid="index-table-pagination" 
                />
              </StyledPaginationContainer>
            </React.Fragment>
          )}
        </Box>
      </Box>

    </React.Fragment>
  );
};

export default DataTable;
